import React, { useEffect, useRef, useState } from 'react';
import { useOrganization, useSession, useUser } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import {
  assistantAudienceProposalDataSchema,
  type AssistantAudienceProposalData,
} from '@charitystack/dashboard-assistant-contracts';
import {
  NATIVE_AUDIENCE_FIELDS,
  nativeAudienceCreateInputSchema,
  nativeAudienceMutationInputSchema,
  type NativeAudienceMutationInput,
  type NativeAudienceDefinition,
  nativeAudienceUpdateInputSchema,
  type AudienceRule,
  type NativeAudienceCatalog,
  type NativeAudienceMutationResult,
} from '@charitystack/schemas';
import './assistant-records.css';
import { getActiveOrganizationContext } from '../lib/organizationContext';
import { isAudienceProposalOutcomeMessage } from '../lib/activityHelpers';
import { recordAudienceProposalOutcome } from '../../../../pages/marketing/nativeAudienceProposalOutcome';
import { nativeAudiencePendingStorageKey, nativeAudiencePersistenceOwner, replaceStorageValue, restoreStoredNativeAudiencePending, type StoredNativeAudiencePending } from '../../../../pages/marketing/nativeAudiencePersistence';
import { Button } from '@/components/ui/button';
import { useDashboardAssistantDock, type DashboardAssistantDockMessage } from '../../../../contexts/DashboardAssistantDockContext';
import {
  createNativeAudience,
  getNativeAudienceCatalog,
  loadNativeAudience,
  updateNativeAudience,
} from '../../../../api/marketing';
import {
  createStoredAudienceProposalHandoff,
  proposalStorageKey,
  proposalHash,
  stableProposalJson,
  storeAudienceProposalHandoff,
  validateAudienceProposalBinding,
} from '../../../../pages/marketing/nativeAudienceProposal';
import { useNativeAudiencePersistenceIdentity } from '../../../../pages/marketing/useNativeAudiencePersistenceIdentity';
import { NativeAudienceSaveOutcome } from '../../../../pages/marketing/NativeAudienceSaveOutcome';
import { describeCondition, kindLabel, orgCurrency } from '../../../../pages/marketing/segment-builder/segmentDefinition';
import { chipsFor } from '../../../../pages/marketing/segment-builder/valueOptions';
import { HandPickedChangeReview, type HandPickedReviewStatus } from './HandPickedChangeReview';

type FieldOptions = NativeAudienceCatalog['fieldOptions'];
const NO_FIELD_OPTIONS: FieldOptions = {};

type ProposalSession = { bindingKey: string; generation: number };
type RecordedOutcome = { outcome: 'saved' | 'conflict'; segmentId: string | null };
type ProposalOperation = { id: number; session: ProposalSession };

export class ProposalOperationCoordinator {
  private session: ProposalSession = { bindingKey: '', generation: 0 };
  private activeOperation: ProposalOperation | null = null;
  private sequence = 0;

  bind(bindingKey: string) {
    if (this.session.bindingKey === bindingKey) return;
    this.session = { bindingKey, generation: this.session.generation + 1 };
    this.activeOperation = null;
  }

  begin(bindingKey: string) {
    if (this.activeOperation || this.session.bindingKey !== bindingKey) return null;
    const operation = { id: ++this.sequence, session: this.session };
    this.activeOperation = operation;
    return operation;
  }

  isCurrent(operation: ProposalOperation) {
    return this.activeOperation === operation && operation.session === this.session;
  }

  finish(operation: ProposalOperation) {
    if (this.activeOperation === operation) this.activeOperation = null;
  }
}

export default function AudienceProposalCard({ value, merchantCountryCode }: { value: unknown; merchantCountryCode?: string | null }) {
  const parsed = assistantAudienceProposalDataSchema.safeParse(value);
  const { organization } = useOrganization();
  const { session } = useSession();
  const { user } = useUser();
  const { organizationId } = getActiveOrganizationContext({
    organization, sessionOrganizationId: session?.lastActiveOrganizationId,
    memberships: user?.organizationMemberships ?? [],
  });
  const { activeSessionId, minimizeForHandoff, sessions } = useDashboardAssistantDock();
  const persistenceIdentity = useNativeAudiencePersistenceIdentity();
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [attemptBlocked, setAttemptBlocked] = useState(false);
  const [mutationResult, setMutationResult] = useState<NativeAudienceMutationResult | null>(null);
  // An update's change against the saved segment. Confirm stays off until it
  // is shown: while it loads, when it fails, and when the segment changed after
  // the draft (owner, 2026-10-15 and 2026-10-17; Greptile P1 on #4575).
  const [reviewStatus, setReviewStatus] = useState<HandPickedReviewStatus>('loading');
  const data = parsed.success ? parsed.data : null;
  // The builder's pick-list labels, so the card reads "Lapsed", not "lapsed". Until they load, or if they can't, values show as stored.
  const [fieldOptions, setFieldOptions] = useState<FieldOptions>(NO_FIELD_OPTIONS);
  const hasProposal = Boolean(data);
  const currency = orgCurrency({ countryCode: merchantCountryCode });
  // A proposal can't be confirmed or opened after its expiry, so both actions
  // turn off then instead of failing on click (QA 2026-10-12, AST-28).
  const expiresAt = data ? Date.parse(data.proposal.expiresAt) : NaN;
  const [expired, setExpired] = useState(() => Number.isFinite(expiresAt) && expiresAt <= Date.now());
  useEffect(() => {
    if (!Number.isFinite(expiresAt)) return undefined;
    const remaining = expiresAt - Date.now();
    setExpired(remaining <= 0);
    if (remaining <= 0) return undefined;
    // setTimeout holds at most about 24.8 days; a later expiry needs no timer.
    if (remaining > 2 ** 31 - 1) return undefined;
    const timer = window.setTimeout(() => setExpired(true), remaining);
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  useEffect(() => {
    if (!hasProposal) return;
    let live = true;
    getNativeAudienceCatalog({ includeFieldOptions: true })
      .then((catalog) => { if (live) setFieldOptions(catalog.fieldOptions ?? NO_FIELD_OPTIONS); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [hasProposal]);
  const bindingKey = `${organizationId ?? ''}:${persistenceIdentity?.actorId ?? ''}:${persistenceIdentity?.sessionId ?? ''}:${persistenceIdentity?.tabId ?? ''}:${activeSessionId ?? ''}:${data?.proposal.proposalId ?? ''}:${data?.proposalHash ?? ''}`;
  const coordinatorRef = useRef<ProposalOperationCoordinator | null>(null);
  const handoffWriterIdRef = useRef(crypto.randomUUID());
  if (!coordinatorRef.current) coordinatorRef.current = new ProposalOperationCoordinator();
  const coordinator = coordinatorRef.current;
  coordinator.bind(bindingKey);
  const attemptKey = data && persistenceIdentity && organizationId
    ? nativeAudiencePendingStorageKey(organizationId, `chat:${activeSessionId}:${data.proposal.proposalId}:${data.proposalHash}`, persistenceIdentity)
    : null;
  // The save outcome another browser recorded in this chat (F-23). Only what
  // this signed-in user's own chat history says; the segment page authorises.
  const recordedOutcome = data
    ? findRecordedOutcome(sessions.find((item) => item.id === activeSessionId)?.historyMessages, data.proposal.proposalId, data.proposalHash)
    : null;
  const attemptBase = data ? { segmentId: data.proposal.segmentId ?? null, revision: data.proposal.expectedRevision ?? null, targetToken: data.proposal.targetToken } : null;

  useEffect(() => {
    setBusy(false);
    setError('');
    setMutationResult(null);
    setAttemptBlocked(false);
    try { setAttempted(Boolean(attemptKey && localStorage.getItem(attemptKey))); }
    catch { setAttempted(true); setAttemptBlocked(true); setError('Save recovery storage is unavailable. Reload before continuing.'); }
  }, [bindingKey]);

  if (!data) {
    return <div className="rounded-md border border-status-red-text/30 bg-status-red-bg p-3 text-sm text-status-red-text">This segment proposal could not be read.</div>;
  }

  function beginOperation() {
    const operation = coordinator.begin(bindingKey);
    if (!operation) return null;
    setBusy(true);
    setError('');
    return operation;
  }

  function isCurrentOperation(operation: ProposalOperation) {
    return coordinator.isCurrent(operation);
  }

  function finishOperation(operation: ProposalOperation) {
    if (!coordinator.isCurrent(operation)) return;
    coordinator.finish(operation);
    setBusy(false);
  }

  async function validateCurrent(operation: ProposalOperation) {
    if (!coordinator.isCurrent(operation)) {
      throw new Error('The signed-in organization, user, or chat session changed. Review the proposal again.');
    }
    if (!organizationId || !persistenceIdentity || !activeSessionId) {
      throw new Error('Open this proposal in its original signed-in chat session.');
    }
    const bound = await validateAudienceProposalBinding({
      value: data,
      organizationId,
      actorId: persistenceIdentity.actorId,
      sessionId: activeSessionId,
    });
    const catalog = await getNativeAudienceCatalog();
    if (bound.proposal.segmentId) {
      const current = await loadNativeAudience(bound.proposal.segmentId);
      if (current.revision !== bound.proposal.expectedRevision) {
        throw new Error('This segment changed after the proposal was prepared. Ask for a fresh proposal.');
      }
      if (current.targetToken !== bound.proposal.targetToken) {
        throw new Error('The marketing account binding changed. Ask for a fresh proposal.');
      }
    } else if (catalog.targetToken !== bound.proposal.targetToken) {
      throw new Error('The marketing account binding changed. Ask for a fresh proposal.');
    }
    if (!coordinator.isCurrent(operation)) {
      throw new Error('The signed-in organization, user, or chat session changed. Review the proposal again.');
    }
    return { bound, catalog };
  }

  async function save() {
    const operation = beginOperation();
    if (!operation) return;
    try {
      if (!persistenceIdentity || !attemptKey || !attemptBase || !data || !organizationId || !activeSessionId) {
        throw new Error('Open this proposal in its original signed-in chat session.');
      }
      const raw = localStorage.getItem(attemptKey);
      let input: NativeAudienceMutationInput;
      if (raw) {
        const stored = restoreStoredNativeAudiencePending(raw, persistenceIdentity, attemptBase);
        if (!stored || stored.input.requestId !== data.proposal.proposalId ||
          stableProposalJson(stored.input.definition) !== stableProposalJson(data.proposal.definition) ||
          stored.input.name !== data.proposal.name || stored.input.description !== data.proposal.description) {
          setAttemptBlocked(true);
          throw new Error('The stored save does not match this proposal. Its bytes were left untouched.');
        }
        // Recovery checks identity and the exact proposal, not a revision,
        // selection or expiry that may have changed after the original save.
        if (data.proposal.organizationId !== organizationId || data.proposal.actorId !== persistenceIdentity.actorId ||
          data.sessionId !== activeSessionId || data.proposalHash !== await proposalHash(data.proposal, activeSessionId)) {
          throw new Error('Open this proposal in its original signed-in chat session.');
        }
        input = stored.input;
      } else {
        const { bound } = await validateCurrent(operation);
        input = nativeAudienceMutationInputSchema.parse({
          intentProtocol: 'native-durable-v1', requestId: bound.proposal.proposalId,
          targetToken: bound.proposal.targetToken, name: bound.proposal.name,
          description: bound.proposal.description, definition: bound.proposal.definition,
          ...(bound.proposal.segmentId ? { segmentId: bound.proposal.segmentId, expectedRevision: bound.proposal.expectedRevision } : {}),
        });
        const stored: StoredNativeAudiencePending = { version: 2, writerId: handoffWriterIdRef.current, owner: nativeAudiencePersistenceOwner(persistenceIdentity), base: attemptBase, input };
        if (!isCurrentOperation(operation)) return;
        if (!replaceStorageValue(localStorage, attemptKey, null, JSON.stringify(stored))) {
          setAttempted(true);
          throw new Error('The save could not be safely stored. Recoverable work was left untouched; reload before continuing.');
        }
      }
      if (!isCurrentOperation(operation)) return;
      setAttempted(true);
      const result = 'segmentId' in input
        ? await updateNativeAudience(nativeAudienceUpdateInputSchema.parse(input))
        : await createNativeAudience(nativeAudienceCreateInputSchema.parse(input));
      if (isCurrentOperation(operation)) {
        setMutationResult(result);
        recordOutcome(result);
      }
    } catch (failure) {
      if (isCurrentOperation(operation)) setError(message(failure));
    } finally {
      finishOperation(operation);
    }
  }

  async function openEditor() {
    if (attempted || (attemptKey && localStorage.getItem(attemptKey))) return;
    const operation = beginOperation();
    if (!operation) return;
    try {
      const { bound } = await validateCurrent(operation);
      if (!isCurrentOperation(operation)) return;
      if (!persistenceIdentity) throw new Error('Open this proposal in its original signed-in chat session.');
      const key = proposalStorageKey(
        bound.proposal.organizationId,
        persistenceIdentity,
        bound.proposal.proposalId,
      );
      const stored = createStoredAudienceProposalHandoff({
        data: bound,
        identity: persistenceIdentity,
        organizationId: bound.proposal.organizationId,
        writerId: handoffWriterIdRef.current,
      });
      if (!storeAudienceProposalHandoff(localStorage, key, stored)) {
        throw new Error('Newer or unreadable editor handoff data was left untouched. Review it before trying again.');
      }
      history.push(
        bound.proposal.segmentId ? `/segments/${bound.proposal.segmentId}/edit` : '/segments/new',
        { nativeProposal: { storageKey: key, requestId: bound.proposal.proposalId, writerId: handoffWriterIdRef.current } },
      );
      // The open dock covered the builder's Segment size panel (AST-04).
      // "Ask CharityStack AI" reopens this chat once afterwards (ADM-198).
      minimizeForHandoff();
    } catch (failure) {
      if (isCurrentOperation(operation)) setError(message(failure));
    } finally {
      finishOperation(operation);
    }
  }

  // Records the outcome in the chat thread so another browser shows it too.
  // Best effort: without it the card offers Confirm, which replays this save.
  function recordOutcome(result: NativeAudienceMutationResult) {
    if (!data || !organizationId || !activeSessionId) return;
    recordAudienceProposalOutcome({
      organizationId,
      target: { threadId: activeSessionId, proposalId: data.proposal.proposalId, proposalHash: data.proposalHash },
      result,
      source: 'AudienceProposalCard',
    });
  }

  const proposal = data.proposal;
  const savedSegmentId = mutationResult?.status === 'saved'
    ? mutationResult.record.id
    : !mutationResult && recordedOutcome?.outcome === 'saved' ? recordedOutcome.segmentId : null;
  const conflictTargetId = mutationResult?.status === 'conflict'
    ? mutationResult.acknowledgement?.targetId
    : !mutationResult && recordedOutcome?.outcome === 'conflict' ? recordedOutcome.segmentId ?? undefined : undefined;
  const conflicted = mutationResult?.status === 'conflict' || (!mutationResult && recordedOutcome?.outcome === 'conflict');
  // A save already attempted stays checkable after expiry: recovery replays it.
  const expiredUnattempted = expired && !attempted;
  // Only an update whose people show as a diff waits for it; recovery of a
  // save already attempted never does.
  // A keep update (ADM-223) has nothing to diff, so Confirm doesn't wait.
  const keepSaved = Boolean(proposal.keepSavedPeople);
  const awaitingChange = Boolean(proposal.segmentId) && !keepSaved && (proposal.definition.kind === 'fixed' || proposal.definition.kind === 'hybrid') && !attempted && reviewStatus !== 'ready';
  return <div className="overflow-hidden rounded-lg border border-edge bg-paper-light">
    <div className="border-b border-edge bg-white px-4 py-3"><p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Segment proposal</p><h3 className="mt-1 text-base font-semibold text-ink">{proposal.name}</h3>{proposal.description && <p className="mt-1 text-sm text-ink-secondary">{proposal.description}</p>}</div>
    <div className="space-y-3 p-4">
      <p className="text-sm text-ink-secondary">{proposal.explanation}</p>
      <dl className="grid grid-cols-2 gap-3 text-sm"><div><dt className="text-xs text-ink-muted">Membership</dt><dd className="mt-1 font-medium text-ink">{membershipKindLabel(proposal.definition)}</dd></div><div><dt className="text-xs text-ink-muted">Conditions</dt><dd className="mt-1 font-medium text-ink">{countConditions(proposal.definition.kind === 'dynamic' || proposal.definition.kind === 'hybrid' ? proposal.definition.rule : undefined)}</dd></div></dl>
      <AudienceMembershipReview
        definition={proposal.definition}
        fieldOptions={fieldOptions}
        currency={currency}
        // An update's rule conditions removed and added, against the saved
        // segment the proposal was drafted from (ADM-228).
        ruleChange={proposal.segmentId ? data.ruleChange : undefined}
        // An update's people as a diff against the saved segment (ADM-203),
        // loaded only while the card can still be confirmed.
        update={proposal.segmentId ? {
          segmentId: proposal.segmentId,
          expectedRevision: proposal.expectedRevision,
          selectionToken: proposal.sourceSelection?.selectionToken,
          threadId: data.sessionId,
          organizationId,
          live: !expired && !attempted && !mutationResult && !recordedOutcome,
          onStatus: setReviewStatus,
          keepSaved,
        } : undefined}
      />
      {error && <p className="text-sm text-status-red-text" role="alert">{error}</p>}
      {expiredUnattempted && !mutationResult && !recordedOutcome && <p className="text-sm text-ink-secondary" role="status">This segment proposal expired. Ask for a fresh proposal.</p>}
      {mutationResult && <NativeAudienceSaveOutcome result={mutationResult} />}
      {!mutationResult && recordedOutcome && <p className="text-sm text-ink-secondary" role="status">{recordedOutcome.outcome === 'saved' ? 'This segment proposal was saved.' : 'This segment changed before or during the save. Reload and review it before making another change.'}</p>}
      {savedSegmentId ? <div className="flex flex-wrap items-center justify-end gap-2"><Button type="button" variant="neutral" size="workspace" onClick={() => history.push(`/segments/${savedSegmentId}`)}>View segment</Button></div> : conflicted ? conflictTargetId && <div className="flex flex-wrap gap-2"><Button type="button" variant="neutral" size="workspace" onClick={() => history.push(`/segments/${conflictTargetId}`)}>View current segment</Button></div> : <div className="flex flex-wrap gap-2">
        <Button type="button" variant="neutral" size="workspace" disabled={busy || attempted || expiredUnattempted} onClick={openEditor}>Review in editor</Button>
        <Button type="button" variant="primary" size="workspace" disabled={busy || attemptBlocked || expiredUnattempted || awaitingChange} onClick={save}>{busy ? 'Saving…' : attempted ? 'Check save result' : proposal.segmentId ? 'Confirm update' : 'Confirm creation'}</Button>
      </div>}
      <p className="text-xs text-ink-muted">This doesn’t send a message, change a campaign or turn on an automation.</p>
    </div>
  </div>;
}

const SEGMENT_ID_PATTERN = /^[A-Za-z0-9_-]{1,256}$/;

function findRecordedOutcome(messages: DashboardAssistantDockMessage[] | undefined, proposalId: string, hash: string): RecordedOutcome | null {
  const entries = messages?.filter((message) => isAudienceProposalOutcomeMessage(message) &&
    message.metadata?.proposalId === proposalId && message.metadata?.proposalHash === hash) ?? [];
  // A save after a conflict (from the builder) is what happened last.
  const entry = entries.find((message) => message.metadata?.outcome === 'saved') ?? entries[0];
  const outcome = entry?.metadata?.outcome;
  if (outcome !== 'saved' && outcome !== 'conflict') return null;
  // The id becomes a dashboard path, so only a plain id is used.
  const rawSegmentId = entry?.metadata?.segmentId;
  const segmentId = typeof rawSegmentId === 'string' && SEGMENT_ID_PATTERN.test(rawSegmentId) ? rawSegmentId : null;
  // A saved outcome is only useful with the segment to open.
  if (outcome === 'saved' && !segmentId) return null;
  return { outcome, segmentId };
}

/** The card's kind word: a hybrid says it has both, rather than the builder's "Updates automatically" (owner, ADM-203). */
function membershipKindLabel(definition: NativeAudienceDefinition) {
  return definition.kind === 'hybrid' ? 'Rules + hand-picked' : kindLabel(definition);
}

function countConditions(rule: AudienceRule | undefined): number {
  if (!rule) return 0;
  if (rule.type === 'all' || rule.type === 'any') return rule.children.reduce((count, child) => count + countConditions(child), 0);
  if (rule.type === 'not') return countConditions(rule.child);
  return 1;
}

function message(error: unknown) {
  return error instanceof Error ? error.message : 'The request could not be completed.';
}


type MembershipUpdate = Omit<React.ComponentProps<typeof HandPickedChangeReview>, 'proposed' | 'hybrid'>;

type RuleChange = NonNullable<AssistantAudienceProposalData['ruleChange']>;

function AudienceMembershipReview({ definition, fieldOptions, currency, update, ruleChange }: { definition: NativeAudienceDefinition; fieldOptions: FieldOptions; currency: string; update?: MembershipUpdate; ruleChange?: RuleChange }) {
  const contacts = definition.kind === 'fixed' || definition.kind === 'hybrid' ? definition.contactIds : [];
  return <section className="assistant-audience-membership" aria-label="Exact segment membership">
    {(definition.kind === 'dynamic' || definition.kind === 'hybrid') && <RuleReview rule={definition.rule} fieldOptions={fieldOptions} currency={currency} />}
    {ruleChange && (ruleChange.groupingChanged || ruleChange.removed.length > 0 || ruleChange.added.length > 0) && <RuleChangeReview change={ruleChange} fieldOptions={fieldOptions} currency={currency} />}
    {(definition.kind === 'fixed' || definition.kind === 'hybrid') && <div>
      {/* Names and counts, never contact IDs: the reviewed list is the preview card above. */}
      {update
        ? <HandPickedChangeReview {...update} proposed={contacts} hybrid={definition.kind === 'hybrid'} />
        : <p>{contacts.length ? `${definition.kind === 'hybrid' ? 'Plus ' : ''}${contacts.length.toLocaleString('en-US')} hand-picked ${contacts.length === 1 ? 'person' : 'people'}` : 'No contacts selected.'}</p>}
    </div>}
  </section>;
}

/**
 * An update's rule change (ADM-228): the saved conditions it removes and the
 * conditions it adds, so the admin sees exactly what Confirm saves. Same
 * lines and classes as the hand-picked change.
 */
function RuleChangeReview({ change, fieldOptions, currency }: { change: RuleChange; fieldOptions: FieldOptions; currency: string }) {
  const lines = (label: string, rules: AudienceRule[]) => rules.length > 0 && <div>
    <p className="assistant-hand-picked-label">{label}</p>
    <ul>{rules.map((rule, index) => <li key={index}><RuleReview rule={rule} fieldOptions={fieldOptions} currency={currency} /></li>)}</ul>
  </div>;
  // A regrouped rule has no condition-by-condition list that reads true (ADM-228 review, F3).
  if (change.groupingChanged) {
    return <div className="assistant-hand-picked-change" aria-label="Rule change">
      <p className="assistant-hand-picked-note">The rule’s grouping changed. The new rule is shown above.</p>
    </div>;
  }
  return <div className="assistant-hand-picked-change" aria-label="Rule change">
    {lines('Conditions removed:', change.removed)}
    {lines('Conditions added:', change.added)}
  </div>;
}

const catalogLabel = (field: string) => NATIVE_AUDIENCE_FIELDS.find((entry) => entry.id === field)?.label ?? field;

// A pick-list value reads as its builder label; any other value stays exact, quoted as stored.
const valueReview = (fieldOptions: FieldOptions, field: string, values: string[]) =>
  chipsFor(fieldOptions[field] ?? [], values).map((chip) => (chip.known ? chip.label : JSON.stringify(chip.label))).join(', ');

function RuleReview({ rule, fieldOptions, currency }: { rule: AudienceRule; fieldOptions: FieldOptions; currency: string }): JSX.Element {
  if ('children' in rule) return <div><p>{rule.type === 'all' ? 'Match all conditions' : 'Match any condition'}</p><ul>{rule.children.map((child, index) => <li key={index}><RuleReview rule={child} fieldOptions={fieldOptions} currency={currency} /></li>)}</ul></div>;
  if ('child' in rule) return <div><p>Exclude when</p><RuleReview rule={rule.child} fieldOptions={fieldOptions} currency={currency} /></div>;
  if (rule.type === 'text') return <p>{catalogLabel(rule.field)} {rule.operator === 'one-of' ? 'is one of' : rule.operator === 'eq' ? 'is' : 'is not'} {valueReview(fieldOptions, rule.field, rule.values)}</p>;
  if (rule.type === 'list') return <p>{catalogLabel(rule.field)} {rule.operator === 'contains' ? 'contain' : 'don’t contain'} {JSON.stringify(rule.value)}</p>;
  if (rule.type === 'date' && rule.operator !== 'age') return <p>{catalogLabel(rule.field)} {absoluteDatePhrase(rule)}</p>;
  // Money in the org's currency, ages as the builder words them (QA 2026-10-12, F-24).
  // The card has no forms list: a form reads by its option's name, else the last four characters of its id.
  return <p>{describeCondition(rule, currency, catalogLabel, fieldOptions)}</p>;
}

const DAY_MS = 24 * 60 * 60 * 1000;
// The rule's own calendar date, read in UTC: the assistant sends UTC
// midnights, and a local-time reading showed "since Mar 3" as Mar 2 west of UTC.
const utcDate = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const isUtcMidnight = (iso: string) => {
  const date = new Date(iso);
  return !Number.isNaN(date.getTime()) && date.getTime() % DAY_MS === 0;
};

type AbsoluteDateRule = Exclude<Extract<AudienceRule, { type: 'date' }>, { operator: 'age' }>;

/** "is after Mar 3, 2026"; a between rule's end is exclusive, so it reads as the day before ("through Sep 30"). */
export function absoluteDatePhrase(rule: AbsoluteDateRule): string {
  if (rule.operator !== 'between') return `is ${rule.operator} ${utcDate(rule.value)}`;
  const end = isUtcMidnight(rule.end)
    ? `through ${utcDate(new Date(Date.parse(rule.end) - DAY_MS).toISOString())}`
    : `up to, not including, ${utcDate(rule.end)}`;
  return `is from ${utcDate(rule.start)} ${end}`;
}
